import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@material-ui/core'
import { REMINDERS_PAUSED_REASONS, resolveRemindersPausedReason } from '@shared/core/billing'
import type { RemindersPausedReason } from '@shared/core/billing'
import { EventRecord, maxMessageLength, UpdateEventBodyDto } from '@shared/core/events'
import { MessageType } from '@shared/core/reminders'
import { extractErrorMessage } from '@shared/util'
import classNames from 'classnames'
import { formatInTimeZone } from 'date-fns-tz'
import Tooltip from 'rc-tooltip'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Controller, FieldErrors, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { firstNonEmpty } from 'src/helpers/first-non-empty'
import { BookingDetailsSection } from 'src/modules/booking/components/booking-details-section'
import generateRandomId from 'src/helpers/generate-random-id'
import { getDayOfWeek } from 'src/helpers/get-day-of-week'
import { getEventDate } from 'src/helpers/get-event-date'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { confirmationVariants } from 'src/modules/confirmation/constants/confirmation-variants'
import {
  Accordion,
  Button,
  FormattedText,
  FormControl,
  Icon,
  Loader,
  Modal,
  PhoneNumberField,
  SelectField,
  TextField,
  ToggleSwitch,
  Typography,
} from 'src/modules/core/components'
import { useDisplayTimezone } from 'src/modules/core/context'
import { createRcTooltipPlacements, RC_TOOLTIP_GAP_PX } from 'src/modules/core/utils/create-rc-tooltip-placements'
import { getDefaultCountryCode, getPhoneAndCountryCode } from 'src/modules/core/utils/phone-input.utils'
import { CantRemoveCustomOffsetModal } from 'src/modules/custom-offsets/cant-remove-custom-offset-modal/cant-remove-custom-offset-modal'
import { useAddCustomOffset } from 'src/modules/custom-offsets/hooks/use-add-custom-offset'
import { useConfirmationOffsets } from 'src/modules/custom-offsets/hooks/use-confirmation-offsets'
import { useFollowUpOffsets } from 'src/modules/custom-offsets/hooks/use-follow-up-offsets'
import { useReminderOffsets } from 'src/modules/custom-offsets/hooks/use-reminder-offsets'
import { useRemoveCustomOffset } from 'src/modules/custom-offsets/hooks/use-remove-custom-offset'
import { eventEditSchema } from 'src/modules/event/schemas/event-edit.schema'
import { formatEventDate } from 'src/modules/event/utils/helpers'
import { generateEventMessage } from 'src/modules/event/utils/message-builder'
import { resolveEventTemplateLanguage } from 'src/modules/event/utils/resolve-event-template-language'
import { setEventRecentlyActivated } from 'src/modules/event/utils/recently-activated-events'
import { InboundSmsEventBadgeConnected } from 'src/modules/inbound-notifications/components/inbound-sms-event-badge-connected'
import { useCalendarsWithTemplates } from 'src/modules/message-templates/hooks/use-calendars-with-templates'
import { useMessageTemplates } from 'src/modules/message-templates/hooks/use-message-templates'
import { useDismissSuggestions } from 'src/modules/phone-suggestions/hooks/use-phone-suggestions'
import { ReminderTypeSection } from 'src/modules/reminder/components/reminder-types/reminder-type-section'
import { ReviewTimingIssuesModal } from 'src/modules/reminder/components/review-timing-issues-modal'
import { ReviewTimingIssuesLogic } from 'src/modules/reminder/components/review-timing-issues-modal/review-timing-issues-logic'
import { buildMessageFieldPlaceholder } from 'src/modules/reminder/utils/dynamic-data-token-hints.utils'
import { useValidatePhoneNumbers } from 'src/modules/sms/sms-compliance/hooks/use-sms-compliance'
import { MessageLanguageNotice } from 'src/modules/user/components/message-language-notice'
import { resolveProfileMessageLanguage } from 'src/modules/user/utils/resolve-profile-message-language'
import { CustomOffsetsService } from 'src/services/custom-offsets.service'
import { useStandingConsentAcceptanceHints } from '../../hooks/use-standing-consent-acceptance-hints'
import { EventAutoActivationHelper } from '../../utils/EventAutoActivationHelper'
import { EventSuggestedNumbersHelper } from '../../utils/EventSuggestedNumbersHelper'
import { RemindersPausedNotice } from '../reminders-paused-notice'
import { AutoActivationCallout } from './auto-activation-callout'
import type {
  EditEventForm,
  EditEventFormData,
  EditEventModalContentProps,
  EditEventModalProps,
} from './edit-event-modal.interface'
import classes from './edit-event-modal.module.scss'
import { EditEventPausedActions } from './edit-event-paused-actions'
import {
  EditEventTimezoneMismatchSentAsPill,
  EditEventTimezonePlaceholderMismatchAlert,
} from './edit-event-timezone-placeholder-mismatch-alert'

const MAX_RECEIVER_NUMBERS = 10

const PAUSED_NOTICE_MESSAGE_IDS: Record<RemindersPausedReason, string> = {
  [REMINDERS_PAUSED_REASONS.quota]: 'event.paused.editor.notice',
  [REMINDERS_PAUSED_REASONS.subscriptionEnded]: 'event.paused.editor.noticeEnded',
  [REMINDERS_PAUSED_REASONS.paymentPaused]: 'event.paused.editor.noticePaymentPaused',
}

function resolvePreviewSenderName(
  formSenderName: string | undefined,
  eventSenderName: string | null | undefined,
): string | undefined {
  return formSenderName ?? eventSenderName ?? undefined
}

function resolvePreviewLocation(
  formLocation: string | undefined,
  eventLocation: string | null | undefined,
): string | undefined {
  return formLocation ?? eventLocation ?? undefined
}

/** Counts code points like the varchar(320) column; the preview lengths are post-truncation. */
function exceedsMaxMessageLength(text?: string | null): boolean {
  return Array.from(text ?? '').length > maxMessageLength
}

interface PendingOffsetRemoval {
  id: string
  offsetInMs: number
  type: MessageType
}

export function EditEventModal({
  onClose,
  modalTitle = <FormattedText id="configuration.editReminderModal.title" />,
  isLoading,
  ...props
}: EditEventModalProps) {
  if (isLoading) {
    return (
      <Modal
        onClose={onClose}
        title={
          <Typography variant="h3" className={classes.modalTitle}>
            {modalTitle}
          </Typography>
        }
      >
        <Loader isLoading={isLoading} />
      </Modal>
    )
  }

  return <EditEventModalContent onClose={onClose} modalTitle={modalTitle} {...props} />
}

export function EditEventModalContent({
  profileData,
  preferences,
  event,
  recentlyActivatedEventId,
  initialPhoneNumbers,
  onClose,
  handleSave,
  modalTitle,
}: EditEventModalContentProps) {
  let defaultCountryCode = getDefaultCountryCode(profileData)
  const remindersPausedReason = resolveRemindersPausedReason(profileData)

  const calendars = useCalendars()
  const { timezone } = useDisplayTimezone()
  const calendar = calendars.data?.find((calendar) => calendar.id === event.calendar)

  const { data: messageTemplates } = useMessageTemplates()
  const { data: calendarsWithTemplates } = useCalendarsWithTemplates()
  const messageTemplateOptions = messageTemplates?.map((t) => ({
    label: t.template_name,
    value: t.id,
  }))

  // Custom offset hooks
  const addCustomOffset = useAddCustomOffset()
  const removeCustomOffset = useRemoveCustomOffset()
  const confirmationOffsets = useConfirmationOffsets()
  const reminderOffsets = useReminderOffsets()
  const followUpOffsets = useFollowUpOffsets()

  // State for pending offset removals
  const [pendingOffsetRemovals, setPendingOffsetRemovals] = useState<PendingOffsetRemoval[]>([])

  // State for cant remove modal
  const [showCantRemoveModal, setShowCantRemoveModal] = useState(false)

  // State to force re-render of phone number fields
  const [phoneNumberKey, setPhoneNumberKey] = useState(0)
  const dismissSuggestions = useDismissSuggestions()
  const [grantStandingConsentFor, setGrantStandingConsentFor] = useState<string[]>([])
  const [standingConsentTouchedPhones, setStandingConsentTouchedPhones] = useState<string[]>([])
  const [isTurnOffLoading, setIsTurnOffLoading] = useState(false)
  const standingConsentPreCheckKeyRef = useRef<string | null>(null)
  const initialActiveStandingConsentRef = useRef<Set<string>>(new Set())

  // Filter out pending removals from timing options
  const filteredConfirmationOffsets = useMemo(() => {
    if (!confirmationOffsets) return []
    const pendingRemovalIds = pendingOffsetRemovals
      .filter((removal) => removal.type === 'confirmation')
      .map((removal) => removal.id)
    return confirmationOffsets.filter((offset) => !pendingRemovalIds.includes(offset.id))
  }, [confirmationOffsets, pendingOffsetRemovals])

  const filteredReminderOffsets = useMemo(() => {
    if (!reminderOffsets) return []
    const pendingRemovalIds = pendingOffsetRemovals
      .filter((removal) => removal.type === 'reminder')
      .map((removal) => removal.id)
    return reminderOffsets.filter((offset) => !pendingRemovalIds.includes(offset.id))
  }, [reminderOffsets, pendingOffsetRemovals])

  const filteredFollowUpOffsets = useMemo(() => {
    if (!followUpOffsets) return []
    const pendingRemovalIds = pendingOffsetRemovals
      .filter((removal) => removal.type === 'follow_up')
      .map((removal) => removal.id)
    return followUpOffsets.filter((offset) => !pendingRemovalIds.includes(offset.id))
  }, [followUpOffsets, pendingOffsetRemovals])

  // Process pending offset removals after successful form submission
  const processPendingOffsetRemovals = useCallback(async (): Promise<void> => {
    if (pendingOffsetRemovals.length === 0) {
      return Promise.resolve()
    }

    const removalPromises = pendingOffsetRemovals.map(({ id }) => removeCustomOffset.mutateAsync(id))

    try {
      await Promise.all(removalPromises)
      setPendingOffsetRemovals([])
    } catch (error) {
      // Note: We don't throw here as the main form operation was successful
      // The user can retry by saving again if needed
      console.error('Failed to remove some custom offsets:', error)
    }
  }, [pendingOffsetRemovals, removeCustomOffset])

  // Use initialPhoneNumbers if provided, otherwise use default logic
  const defaultReceiverNumbers = useMemo(() => {
    if (initialPhoneNumbers?.length) {
      return initialPhoneNumbers.map((n) => n.phone)
    }

    const eventPhoneNumbers = Array.from(
      new Set([...(event?.receiver_numbers ?? []), ...EventSuggestedNumbersHelper.getPendingSuggestions(event)]),
    ).filter(Boolean)

    if (eventPhoneNumbers.length > 0) {
      return eventPhoneNumbers
    }
    return profileData.usage_mode === 'personal' ? profileData.phone_numbers : []
  }, [initialPhoneNumbers, profileData.usage_mode, profileData.phone_numbers, event])

  if (defaultReceiverNumbers.length === 0) {
    defaultReceiverNumbers.push('')
  }

  // hooks
  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    setValue,
    formState: { isDirty, errors, isSubmitting },
  } = useForm<EditEventForm & { formError?: string }>({
    defaultValues: {
      message_template_id: event?.message_template_id,
      sender_name: event?.sender_name,
      location: event?.location,
      receiver_numbers: defaultReceiverNumbers.map((n) => ({
        id: generateRandomId(),
        ...getPhoneAndCountryCode(n, defaultCountryCode),
      })),
      confirmation_enabled: event?.confirmation_enabled ?? false,
      confirmation_message: event?.confirmation_message ?? '',
      confirmation_offsets:
        firstNonEmpty(
          event.reminders?.data.filter((r) => r.message_type === 'confirmation').map((r) => r.time_before),
          event.confirmation_offsets,
        ) ?? [],
      reminder_enabled: event?.reminder_enabled ?? false,
      reminder_message: event?.reminder_message ?? '',
      reminder_offsets:
        firstNonEmpty(
          event.reminders?.data.filter((r) => r.message_type === 'reminder').map((r) => r.time_before),
          event.reminder_offsets,
        ) ?? [],
      follow_up_enabled: event?.follow_up_enabled ?? false,
      follow_up_message: event?.follow_up_message ?? '',
      follow_up_offsets:
        firstNonEmpty(
          event.reminders?.data.filter((r) => r.message_type === 'follow_up').map((r) => r.time_before),
          event.follow_up_offsets,
        ) ?? [],
    },
    // @ts-ignore
    resolver: yupResolver(eventEditSchema),
  })
  const [reviewTimingIssuesState, setReviewTimingIssuesState] = useState<'closed' | 'open' | 'send'>('closed')

  const { formatMessage } = useIntl()

  const eventDate = useMemo(() => getEventDate(event), [event])
  const originalEventDate = useMemo(
    () => new Date(Number(event.original_event_date ?? event.event_date)),
    [event.original_event_date, event.event_date],
  )

  const isPastEvent = useMemo(() => new Date() > eventDate, [eventDate])

  const amountOfReceiverNumbers = defaultReceiverNumbers.length || 1

  const sender_name = useWatch({
    control,
    name: 'sender_name',
    defaultValue: event?.sender_name ?? '',
  })
  const location = useWatch({
    control,
    name: 'location',
    defaultValue: event?.location ?? '',
  })
  const confirmation_message = useWatch({
    control,
    name: 'confirmation_message',
    defaultValue: event?.confirmation_message ?? '',
  })
  const reminder_message = useWatch({
    control,
    name: 'reminder_message',
    defaultValue: event?.reminder_message ?? '',
  })
  const follow_up_message = useWatch({
    control,
    name: 'follow_up_message',
    defaultValue: event?.follow_up_message ?? '',
  })
  const confirmation_enabled = watch('confirmation_enabled')
  const reminder_enabled = watch('reminder_enabled')
  const follow_up_enabled = watch('follow_up_enabled')

  const resolvedSenderName = resolvePreviewSenderName(sender_name, event?.sender_name)
  const resolvedLocation = resolvePreviewLocation(location, event?.location)

  const previewProfile = useMemo(
    () => ({
      ...profileData,
      default_message_template: {
        ...profileData.default_message_template,
        sender_name: resolvedSenderName,
        location: resolvedLocation,
      },
    }),
    [profileData, resolvedSenderName, resolvedLocation],
  )
  const selectedTemplateId = useWatch({
    control,
    name: 'message_template_id',
    defaultValue: event?.message_template_id,
  })
  const { language: templateLanguage, origin: templateOrigin } = useMemo(
    () =>
      resolveEventTemplateLanguage({
        selectedTemplateId,
        event,
        templates: messageTemplates,
        calendarsWithTemplates,
      }),
    [selectedTemplateId, event, messageTemplates, calendarsWithTemplates],
  )
  const {
    language: messageLanguage,
    source: messageLanguageSource,
    countryCode: messageLanguageCountryCode,
  } = useMemo(() => resolveProfileMessageLanguage(profileData, templateLanguage), [profileData, templateLanguage])

  // Watch phone number fields specifically for validation
  const watchedPhoneNumbers = watch('receiver_numbers')

  const standingConsentCandidates = useMemo(() => {
    const phones = watchedPhoneNumbers?.map((entry) => entry.phone).filter(Boolean) ?? []
    return EventAutoActivationHelper.getStandingConsentCandidates(
      event,
      phones,
      Boolean(preferences.user_preferences?.auto_activate_when_clear),
      isPastEvent,
    )
  }, [preferences.user_preferences?.auto_activate_when_clear, event, isPastEvent, watchedPhoneNumbers])

  const { data: standingConsentHints } = useStandingConsentAcceptanceHints(
    standingConsentCandidates,
    standingConsentCandidates.length > 0,
  )

  useEffect(() => {
    standingConsentPreCheckKeyRef.current = null
    initialActiveStandingConsentRef.current = new Set()
    setGrantStandingConsentFor([])
    setStandingConsentTouchedPhones([])
  }, [event.id])

  useEffect(() => {
    const preCheckKey = standingConsentCandidates.join('|')
    if (
      !standingConsentHints ||
      standingConsentCandidates.length === 0 ||
      standingConsentPreCheckKeyRef.current === preCheckKey
    ) {
      return
    }

    standingConsentPreCheckKeyRef.current = preCheckKey
    const phonesWithActiveConsent = standingConsentCandidates.filter((phone) => standingConsentHints.active[phone])
    initialActiveStandingConsentRef.current = new Set(phonesWithActiveConsent)
  }, [standingConsentCandidates, standingConsentHints])

  const toggleStandingConsent = useCallback((phone: string, checked: boolean) => {
    setStandingConsentTouchedPhones((current) => (current.includes(phone) ? current : [...current, phone]))
    setGrantStandingConsentFor((current) =>
      checked ? [...new Set([...current, phone])] : current.filter((value) => value !== phone),
    )
  }, [])

  // Debounced phone validation
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [phoneNumbersToValidate, setPhoneNumbersToValidate] = useState<string[]>([])
  const [phoneValidationErrors, setPhoneValidationErrors] = useState<Record<number, string>>({})

  // Extract phone numbers for validation
  const phoneNumbersForValidation = useMemo(() => {
    return (
      watchedPhoneNumbers?.map((n) => n.phone).filter((phone) => phone && phone.trim() !== '' && phone !== '+') || []
    )
  }, [watchedPhoneNumbers])

  useEffect(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }

    if (phoneNumbersForValidation.length === 0) {
      setPhoneValidationErrors({})
      setPhoneNumbersToValidate([])
      return
    }

    validationTimeoutRef.current = setTimeout(() => {
      setPhoneNumbersToValidate(phoneNumbersForValidation)
    }, 300)

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [phoneNumbersForValidation])

  // Use the validation hook
  const { data: validationResults } = useValidatePhoneNumbers(phoneNumbersToValidate, {
    enabled: phoneNumbersToValidate.length > 0,
  })

  // Process validation results and map to field indices
  useEffect(() => {
    if (!validationResults || !watchedPhoneNumbers) {
      return
    }

    const newErrors: Record<number, string> = {}

    validationResults.forEach((result) => {
      if (!result.isValid && result.invalidReason) {
        const fieldIndex = watchedPhoneNumbers.findIndex((field) => {
          const fieldPhone = field.phone || ''
          const resultPhone = result.phoneNumber || ''
          return fieldPhone === resultPhone
        })

        if (fieldIndex !== -1) {
          newErrors[fieldIndex] = result.errorMessage
        }
      }
    })

    setPhoneValidationErrors(newErrors)
  }, [validationResults, watchedPhoneNumbers])

  const {
    message: confirmationMessagePreview,
    messageLength: confirmationMessageLength,
    segmentsCount: confirmationSegmentsCount,
  } = useMemo(
    () =>
      generateEventMessage(
        'confirmation',
        previewProfile,
        preferences,
        {
          ...event,
          confirmation_message,
          sender_name: resolvedSenderName,
          location: resolvedLocation,
        },
        calendar,
        templateLanguage,
      ),
    [
      previewProfile,
      preferences,
      confirmation_message,
      resolvedSenderName,
      resolvedLocation,
      event,
      calendar,
      templateLanguage,
    ],
  )
  const {
    message: reminderMessagePreview,
    messageLength: reminderMessageLength,
    segmentsCount: reminderSegmentsCount,
  } = useMemo(
    () =>
      generateEventMessage(
        'reminder',
        previewProfile,
        preferences,
        {
          ...event,
          reminder_message,
          sender_name: resolvedSenderName,
          location: resolvedLocation,
        },
        calendar,
        templateLanguage,
      ),
    [
      previewProfile,
      preferences,
      reminder_message,
      resolvedSenderName,
      resolvedLocation,
      event,
      calendar,
      templateLanguage,
    ],
  )
  const {
    message: followUpMessagePreview,
    messageLength: followUpMessageLength,
    segmentsCount: followUpSegmentsCount,
  } = useMemo(
    () =>
      generateEventMessage(
        'follow_up',
        previewProfile,
        preferences,
        {
          ...event,
          follow_up_message,
          sender_name: resolvedSenderName,
          location: resolvedLocation,
        },
        calendar,
        templateLanguage,
      ),
    [
      previewProfile,
      preferences,
      follow_up_message,
      resolvedSenderName,
      resolvedLocation,
      event,
      calendar,
      templateLanguage,
    ],
  )

  const confirmationMessageFieldPlaceholder = useMemo(
    () => buildMessageFieldPlaceholder('confirmation', messageLanguage),
    [messageLanguage],
  )
  const reminderMessageFieldPlaceholder = useMemo(
    () => buildMessageFieldPlaceholder('reminder', messageLanguage),
    [messageLanguage],
  )
  const followUpMessageFieldPlaceholder = useMemo(
    () => buildMessageFieldPlaceholder('follow_up', messageLanguage),
    [messageLanguage],
  )

  const handleToggle = (field: keyof EditEventForm) => (enabled: boolean) => {
    setValue(field, enabled, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  const handleAddCustomOffset = async (offsetInMs: number, type: MessageType): Promise<void> => {
    const key = `${type}_offsets` as keyof EditEventForm
    return new Promise((resolve, reject) => {
      addCustomOffset.mutate(
        { offset: offsetInMs, messageType: type },
        {
          onSuccess: () => {
            setValue(key, [...new Set([...((watch(key) as number[]) || []), offsetInMs])], {
              shouldDirty: true,
              shouldTouch: true,
            })
            resolve()
          },
          onError: (error: unknown) => {
            reject(new Error(extractErrorMessage(error, 'Failed to add custom offset')))
          },
        },
      )
    })
  }

  const handleRemoveCustomOffset = async (id: string, offsetInMs: number, type: MessageType): Promise<void> => {
    const canRemove = await CustomOffsetsService.canRemoveCustomOffset(id)
    if (!canRemove) {
      setShowCantRemoveModal(true)
      return Promise.resolve()
    }

    // Proceed with removal
    const key = `${type}_offsets` as keyof EditEventForm
    setValue(
      key,
      (watch(key) as number[]).filter((v) => v !== offsetInMs),
      { shouldDirty: true, shouldTouch: true },
    )
    setPendingOffsetRemovals((prev) => [...prev, { id, offsetInMs, type }])
    return Promise.resolve()
  }

  const {
    fields: phoneNumberFields,
    append: addPhoneNumber,
    remove: removePhoneNumber,
  } = useFieldArray({
    control,
    name: 'receiver_numbers',
  })

  function handleAddPhoneNumber() {
    if (phoneNumberFields.length < MAX_RECEIVER_NUMBERS) {
      addPhoneNumber({
        id: generateRandomId(),
        country: defaultCountryCode,
        phone: '',
      })
    }
  }

  function handleRemovePhoneNumber(index: number) {
    // Get current form values to avoid stale data
    const currentReceiverNumbers = watch('receiver_numbers')
    const phoneToRemove = currentReceiverNumbers[index]

    // Check if the phone number being removed is a suggested number
    if (phoneToRemove?.phone && EventSuggestedNumbersHelper.isSuggestedNumber(event, phoneToRemove.phone)) {
      // Call dismiss suggestions mutation for suggested numbers
      dismissSuggestions.mutate({
        eventId: event.id,
        title: event.title || '',
        dayOfWeek: getDayOfWeek(eventDate),
        removedNumbers: [phoneToRemove.phone],
        clientTimestamp: new Date().toISOString(),
      })
    }

    if (index === 0 && currentReceiverNumbers.length === 1) {
      // If it's the first and only phone number, clear it instead of removing
      const currentPhone = currentReceiverNumbers[index]
      setValue(
        `receiver_numbers.${index}`,
        {
          ...currentPhone,
          phone: '',
          country: defaultCountryCode,
        },
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      )
      // Clear validation error for this field
      setPhoneValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[index]
        return newErrors
      })
      // Force re-render of phone number fields
      setPhoneNumberKey((prev) => prev + 1)
    } else {
      // Otherwise, remove the phone number normally
      removePhoneNumber(index)
    }
  }

  const onSubmit = useCallback(
    async (data: EditEventFormData) => {
      if (isDirty || !data.active) {
        if (exceedsMaxMessageLength(data.confirmation_message)) {
          return setError('confirmation_message', {
            type: 'custom',
            message: `The Confirmation message must be shorter than ${maxMessageLength} characters`,
          })
        }

        if (exceedsMaxMessageLength(data.reminder_message)) {
          return setError('reminder_message', {
            type: 'custom',
            message: `The Reminder message must be shorter than ${maxMessageLength} characters`,
          })
        }

        if (exceedsMaxMessageLength(data.follow_up_message)) {
          return setError('follow_up_message', {
            type: 'custom',
            message: `The Follow-up message must be shorter than ${maxMessageLength} characters`,
          })
        }

        if (isPastEvent) {
          return setError('confirmation_offsets', {
            type: 'custom',
            message: 'Event is in the past, cannot set reminders',
          })
        }

        const receiverNumbers = data.receiver_numbers.filter((n) => n.phone && n.phone !== '+')
        if (!receiverNumbers.length) {
          return setError('receiver_numbers', {
            type: 'custom',
            message: 'At least one phone number is required',
          })
        }

        // Check for phone validation errors
        if (Object.keys(phoneValidationErrors).length > 0) {
          return setError('receiver_numbers', {
            type: 'custom',
            message: 'Please fix phone number validation errors before saving',
          })
        }

        const result = ReviewTimingIssuesLogic.evaluate({
          confirmation_enabled: data.confirmation_enabled,
          confirmation_offsets: data.confirmation_offsets,
          reminder_enabled: data.reminder_enabled,
          reminder_offsets: data.reminder_offsets,
          event_date: eventDate,
        })
        if (reviewTimingIssuesState !== 'send') {
          if (!result.allClear) {
            setReviewTimingIssuesState('open')
            return
          }
        }

        let send_reminder_immediately = false
        if (reviewTimingIssuesState === 'send') {
          send_reminder_immediately = result.offerImmediateConfirmation || result.offerImmediateReminder
          setReviewTimingIssuesState('closed')
        }

        const revokeStandingConsentFor = [...initialActiveStandingConsentRef.current].filter(
          (phone) =>
            standingConsentCandidates.includes(phone) &&
            standingConsentTouchedPhones.includes(phone) &&
            !grantStandingConsentFor.includes(phone),
        )

        const payload: Partial<EventRecord> &
          Pick<UpdateEventBodyDto, 'grant_standing_consent_for' | 'revoke_standing_consent_for'> = {
          ...data,
          id: event.id, // Will either refer to individual event or parent recurring event
          active: true,
          send_reminder_immediately,
          confirmation_offsets: data.confirmation_offsets.map(Number),
          receiver_numbers: receiverNumbers.map((n) => n.phone),
          ...(grantStandingConsentFor.length > 0 ? { grant_standing_consent_for: grantStandingConsentFor } : {}),
          ...(revokeStandingConsentFor.length > 0 ? { revoke_standing_consent_for: revokeStandingConsentFor } : {}),
        }

        const wasInactive = !event.active
        const graceEventId = recentlyActivatedEventId ?? event.id

        try {
          await handleSave(payload.id, payload)
          await processPendingOffsetRemovals()

          if (wasInactive) {
            setEventRecentlyActivated(graceEventId)
          }
        } catch (error) {
          return setError('formError', {
            type: 'custom',
            message: error.message,
          })
        }

        return
      }

      onClose()
    },
    [
      isDirty,
      eventDate,
      setError,
      handleSave,
      onClose,
      event,
      recentlyActivatedEventId,
      isPastEvent,
      reviewTimingIssuesState,
      processPendingOffsetRemovals,
      phoneValidationErrors,
      grantStandingConsentFor,
      standingConsentCandidates,
      standingConsentTouchedPhones,
    ],
  )

  const handleTurnOffAutoActivation = useCallback(
    async (
      payload: Pick<UpdateEventBodyDto, 'active' | 'revoke_auto_activation' | 'standing_consent_revoke_source'>,
    ) => {
      setIsTurnOffLoading(true)
      try {
        await handleSave(event.id, { id: event.id, ...payload })
      } catch (error) {
        setError('formError', {
          type: 'custom',
          message: error.message,
        })
      } finally {
        setIsTurnOffLoading(false)
      }
    },
    [handleSave, event.id, setError],
  )

  function customOnSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (errors && Object.keys(errors).length > 0) {
      console.log(JSON.stringify(errors))
    }
    handleSubmit(onSubmit)(e)
  }

  const handleCloseReviewTimingIssuesModal = useCallback(() => {
    setReviewTimingIssuesState('closed')
  }, [])

  const handleSendReviewTimingIssues = useCallback(() => {
    setReviewTimingIssuesState('send')
  }, [])

  useEffect(() => {
    if (reviewTimingIssuesState === 'send') {
      handleSubmit(onSubmit)()
    }
  }, [reviewTimingIssuesState, handleSubmit, onSubmit])

  const acceptanceVariant = useMemo(() => {
    if (
      !profileData?.appointment_confirmation ||
      !event.active ||
      event.confirmed === null ||
      (event.confirmed === 'pending' && isPastEvent)
    ) {
      return null
    }

    return (
      {
        pending: confirmationVariants.waiting,
        confirmed: confirmationVariants.accept,
        cancelled: confirmationVariants.reject,
      }[event.confirmed] ?? null
    )
  }, [profileData?.appointment_confirmation, event.active, event.confirmed, isPastEvent])

  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false)
  const handleTemplateChange = useCallback(
    (templateId: string) => {
      const selectedTemplate = messageTemplates?.find((t) => t.id === templateId)
      if (selectedTemplate) {
        setIsApplyingTemplate(true)
        setTimeout(() => {
          setValue('sender_name', selectedTemplate.sender_name)
          setValue('location', selectedTemplate.location)
          setValue('confirmation_enabled', selectedTemplate.confirmation_enabled)
          setValue('confirmation_message', selectedTemplate.confirmation_message)
          setValue('confirmation_offsets', selectedTemplate.confirmation_offsets)
          setValue('reminder_enabled', selectedTemplate.reminder_enabled)
          setValue('reminder_message', selectedTemplate.reminder_message)
          setValue('reminder_offsets', selectedTemplate.reminder_offsets)
          setValue('follow_up_enabled', selectedTemplate.follow_up_enabled)
          setValue('follow_up_message', selectedTemplate.follow_up_message)
          setValue('follow_up_offsets', selectedTemplate.follow_up_offsets)
          setTimeout(() => setIsApplyingTemplate(false), 0)
        }, 0)
      }
    },
    [messageTemplates, setValue],
  )

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (
        !isApplyingTemplate &&
        value.message_template_id &&
        [
          'sender_name',
          'location',
          'confirmation_enabled',
          'confirmation_message',
          'confirmation_offsets',
          'reminder_enabled',
          'reminder_message',
          'reminder_offsets',
          'follow_up_enabled',
          'follow_up_message',
          'follow_up_offsets',
        ].includes(name)
      ) {
        setValue('message_template_id', null)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setValue, isApplyingTemplate])

  return (
    <>
      <Modal
        style={{ maxWidth: '720px' }}
        onClose={onClose}
        title={
          <Typography variant="h3" className={classes.modalTitle}>
            {modalTitle}
          </Typography>
        }
      >
        <form
          className={classNames(classes.root)}
          // @ts-ignore
          onSubmit={customOnSubmit}
        >
          <div className={classes.header}>
            <div>
              <div className={classes.titleContainer}>
                <Typography variant="h2" className={classes.title}>
                  {event.title || <FormattedText id="reminder.noTitle" />}
                </Typography>
                {event.has_unread_inbound_sms ? (
                  <InboundSmsEventBadgeConnected
                    eventId={event.id}
                    previewFromEvent={event.unread_inbound_sms_preview}
                  />
                ) : null}
                {event.is_booked_appointment && (
                  <div title="Booked appointment">
                    <Icon name="calendarAlt" className={classes.bookingIcon} />
                  </div>
                )}
              </div>
              <div className={classes.eventDateRow}>
                <Typography variant="body" className={classes.regular}>
                  {formatEventDate(eventDate, profileData.google_calendar_date_order, timezone)}
                  {', '}
                  {event.all_day ? (
                    <FormattedText id="reminder.allDay" />
                  ) : (
                    formatInTimeZone(originalEventDate, timezone, 'h:mma')
                  )}
                </Typography>
                <EditEventTimezoneMismatchSentAsPill
                  event={event}
                  profileData={profileData}
                  preferences={preferences}
                  calendar={calendar}
                  confirmation_message={confirmation_message}
                  reminder_message={reminder_message}
                  follow_up_message={follow_up_message}
                  confirmation_enabled={Boolean(confirmation_enabled)}
                  reminder_enabled={Boolean(reminder_enabled)}
                  follow_up_enabled={Boolean(follow_up_enabled)}
                />
              </div>
              {event.event_location ? (
                <Typography variant="body" className={classes.eventLocation}>
                  {event.event_location}
                </Typography>
              ) : null}
            </div>
            <div className={classes.statusWrapper}>
              {acceptanceVariant && (
                <div
                  className={classNames(classes.statusBadge, {
                    [classes.statusBadgeAccept]: acceptanceVariant === confirmationVariants.accept,
                    [classes.statusBadgeWaiting]: acceptanceVariant === confirmationVariants.waiting,
                    [classes.statusBadgeRejected]: acceptanceVariant === confirmationVariants.reject,
                  })}
                >
                  {acceptanceVariant === 'accept' && (
                    <Typography variant="body" className={classes.statusText}>
                      <FormattedText id="configuration.editReminder.status.accepted" />
                    </Typography>
                  )}
                  {acceptanceVariant === 'waiting' && (
                    <Typography variant="body" className={classes.statusText}>
                      <FormattedText id="configuration.editReminder.status.waiting" />
                    </Typography>
                  )}
                  {acceptanceVariant === 'reject' && (
                    <Typography variant="body" className={classes.statusText}>
                      <FormattedText id="configuration.editReminder.status.rejected" />
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </div>
          {remindersPausedReason && (
            <RemindersPausedNotice
              className={classes.pausedNotice}
              aside={
                remindersPausedReason === REMINDERS_PAUSED_REASONS.quota && (
                  <FormattedText
                    id="event.paused.editor.usage"
                    values={{
                      sent: profileData.reminders_sent_during_billing_cycle,
                      limit: profileData.effective_free_reminders_limit,
                    }}
                  />
                )
              }
            >
              <FormattedText
                id={PAUSED_NOTICE_MESSAGE_IDS[remindersPausedReason]}
                values={{
                  limit: profileData.effective_free_reminders_limit,
                  strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
                }}
              />
            </RemindersPausedNotice>
          )}
          {(errors.formError || event.errors.length > 0) && (
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gridGap={6} marginBottom={2}>
              <Icon name="eventCardError" />
              <span className={classes.error}>{errors.formError?.message || event.errors.join(', ')}</span>
            </Box>
          )}
          <AutoActivationCallout
            event={event}
            onTurnOffReminder={handleTurnOffAutoActivation}
            isTurnOffLoading={isTurnOffLoading}
          />
          {event.is_booked_appointment && <BookingDetailsSection eventId={event.id} />}
          <div className={classes.phoneNumbers}>
            {phoneNumberFields.map((phone, index) => {
              const phoneE164 = watch(`receiver_numbers.${index}.phone`) || ''
              const isStandingConsentCandidate = standingConsentCandidates.includes(phoneE164)
              const showAcceptanceNudge = Boolean(standingConsentHints?.showAcceptanceNudge[phoneE164])
              const standingConsentTooltipBase = formatMessage({
                id: 'autoActivation.standingConsent.tooltip',
              })
              const standingConsentTooltipNudge = formatMessage({
                id: 'autoActivation.standingConsent.preCheckHint',
              })
              const standingConsentTooltipAriaLabel = showAcceptanceNudge
                ? `${standingConsentTooltipBase} ${standingConsentTooltipNudge}`
                : standingConsentTooltipBase
              const customerNumberLabel = `${formatMessage({
                id: 'configuration.editReminderModal.label.phoneNumber',
              })} ${index + 1}`

              const isStandingConsentOn = grantStandingConsentFor.includes(phoneE164)
              const hasStandingConsentBeenTouched = standingConsentTouchedPhones.includes(phoneE164)
              const showStandingConsentLabelShimmer =
                isStandingConsentCandidate && !isStandingConsentOn && !hasStandingConsentBeenTouched

              const standingConsentControls = (
                <Tooltip
                  placement="top"
                  builtinPlacements={createRcTooltipPlacements(RC_TOOLTIP_GAP_PX)}
                  trigger={['hover', 'focus']}
                  overlay={
                    <Typography variant="span" className={classes.standingConsentTooltipText}>
                      <FormattedText id="autoActivation.standingConsent.tooltip" />
                      {showAcceptanceNudge && (
                        <span className={classes.standingConsentTooltipNudge}>
                          <FormattedText id="autoActivation.standingConsent.preCheckHint" />
                        </span>
                      )}
                    </Typography>
                  }
                  overlayClassName={classes.tooltipOverlay}
                >
                  <div className={classes.standingConsentControls} data-testid={`standing-consent-info-${phoneE164}`}>
                    <span
                      className={classes.standingConsentToggleLabelTrigger}
                      tabIndex={0}
                      aria-label={standingConsentTooltipAriaLabel}
                    >
                      <Typography
                        variant="body"
                        className={classNames(classes.standingConsentToggleLabel, {
                          [classes.standingConsentToggleLabelShimmer]: showStandingConsentLabelShimmer,
                        })}
                      >
                        <FormattedText id="autoActivation.standingConsent.toggleLabel" />
                      </Typography>
                    </span>
                    <ToggleSwitch
                      id={`standing-consent-${phoneE164}`}
                      checked={grantStandingConsentFor.includes(phoneE164)}
                      onChange={(changeEvent) =>
                        toggleStandingConsent(phoneE164, (changeEvent.target as HTMLInputElement).checked)
                      }
                      data-testid={`standing-consent-toggle-${phoneE164}`}
                    />
                  </div>
                </Tooltip>
              )

              return (
                <FormControl
                  label={isStandingConsentCandidate ? undefined : customerNumberLabel}
                  error={
                    errors.receiver_numbers && errors.receiver_numbers[index]?.phone?.message
                      ? errors.receiver_numbers[index]?.phone?.message
                      : phoneValidationErrors[index]
                  }
                  className={classes.textFieldWrapper}
                  key={phone.id}
                >
                  <Controller
                    control={control}
                    name={`receiver_numbers.${index}`}
                    render={(props) => {
                      const phoneNumberField = (
                        <PhoneNumberField
                          key={`${phone.id}-${phoneNumberKey}`}
                          disabled={isPastEvent}
                          value={props.field.value?.phone || ''}
                          defaultCountry={defaultCountryCode}
                          error={
                            errors.receiver_numbers && errors.receiver_numbers[index]?.phone.message
                              ? errors.receiver_numbers[index]?.phone.message
                              : phoneValidationErrors[index]
                          }
                          onChange={(value: string, countryCode: string | null) => {
                            props.field.onChange({
                              country: countryCode,
                              phone: `+${value.replace(/\D/g, '')}`,
                            })

                            // Trigger phone validation after field update
                            setTimeout(() => {
                              const currentReceiverNumbers = watch('receiver_numbers')
                              const phoneNumbers =
                                currentReceiverNumbers
                                  ?.map((n) => n.phone)
                                  .filter((phone) => phone && phone.trim() !== '' && phone !== '+') || []

                              if (validationTimeoutRef.current) {
                                clearTimeout(validationTimeoutRef.current)
                              }

                              validationTimeoutRef.current = setTimeout(() => {
                                setPhoneNumbersToValidate(phoneNumbers)
                              }, 600)
                            }, 0)
                          }}
                          mode={
                            EventSuggestedNumbersHelper.isSuggestedNumber(event, props.field.value?.phone)
                              ? 'suggestion'
                              : 'default'
                          }
                        />
                      )

                      const removePhoneButton = (
                        <span className={classes.phoneNumberRemoveCell} onClick={() => handleRemovePhoneNumber(index)}>
                          <Icon name="close" className={classes.removeButton} />
                        </span>
                      )

                      if (isStandingConsentCandidate) {
                        return (
                          <div className={classes.phoneNumberRowGrid}>
                            <div className={classes.phoneNumberLabelRow}>
                              <span className={classes.phoneNumberRowLabel}>{customerNumberLabel}</span>
                              {standingConsentControls}
                            </div>
                            <div className={classes.phoneNumberInputCell}>{phoneNumberField}</div>
                            {removePhoneButton}
                          </div>
                        )
                      }

                      return (
                        <div className={classes.phoneNumberInputRow}>
                          {phoneNumberField}
                          {removePhoneButton}
                        </div>
                      )
                    }}
                  />
                </FormControl>
              )
            })}
            {amountOfReceiverNumbers < MAX_RECEIVER_NUMBERS && (
              <Typography variant="body" className={classes.textButton} onClick={handleAddPhoneNumber}>
                + <FormattedText id="configuration.editReminderModal.textButton.addPhone" />
              </Typography>
            )}
          </div>
          <hr className={classes.divider} />
          <FormControl label="Select a template to use" className={classes.textFieldWrapper}>
            <Controller
              control={control}
              name="message_template_id"
              render={({ field: { onChange, value } }) => (
                <SelectField
                  isDisabled={isPastEvent}
                  value={value ? messageTemplateOptions?.find((t) => t.value === value) : null}
                  onChange={(selected) => {
                    onChange(selected?.value)
                    handleTemplateChange(selected?.value)
                  }}
                  options={messageTemplateOptions}
                  styles={{ control: (base) => ({ ...base, padding: '9px 5px', border: 'none' }) }}
                  closeMenuOnSelect
                  className={classes.selectField}
                />
              )}
            />
          </FormControl>
          <Accordion
            Title={({ isActive }) => (
              <Typography variant="body" className={classes.accordionTitle}>
                {isActive ? (
                  <FormattedText id="configuration.editReminderModal.accordion.active" />
                ) : (
                  <FormattedText id="configuration.editReminderModal.accordion.notActive" />
                )}
                <Icon
                  name="arrowLeft"
                  className={classNames(classes.accordionIcon, {
                    [classes.accordionIconActive]: isActive,
                  })}
                />
              </Typography>
            )}
            body={
              <>
                <TextField
                  disabled={isPastEvent}
                  wrapperClassName={classes.textFieldWrapper}
                  label={formatMessage({
                    id: 'configuration.step1.label.from',
                  })}
                  placeholder={'Your business name (e.g., "ABC Hair Inc")'}
                  {...register('sender_name')}
                  error={errors.sender_name?.message}
                />
                <TextField
                  disabled={isPastEvent}
                  wrapperClassName={classes.textFieldWrapper}
                  label="Meeting Location"
                  placeholder={'Specify a location (e.g., "Office" or "Gym"). You can also use a link.'}
                  Element="textarea"
                  {...register('location')}
                  error={errors.location?.message}
                />

                <div className={classes.reminderTypes}>
                  <Typography variant="h3" className={classes.messagesTitle}>
                    Messages
                  </Typography>

                  <MessageLanguageNotice
                    language={messageLanguage}
                    source={messageLanguageSource}
                    countryCode={messageLanguageCountryCode}
                    templateOrigin={templateOrigin}
                    usageMode={profileData.usage_mode}
                    hasUnsavedChanges={isDirty || pendingOffsetRemovals.length > 0}
                  />

                  {!watch('confirmation_enabled') && !watch('reminder_enabled') && !watch('follow_up_enabled') && (
                    <Typography variant="body" className={classes.noMessageTypesError}>
                      At least one message type must be enabled.
                    </Typography>
                  )}

                  <ReminderTypeSection
                    type="confirmation"
                    title="Confirmation"
                    description="Message to send after the reminder is created, or if the date/time of the appointment changes."
                    checked={watch('confirmation_enabled')}
                    disabled={isPastEvent}
                    messageField="confirmation_message"
                    messageFieldPlaceholder={confirmationMessageFieldPlaceholder}
                    messageLanguage={messageLanguage}
                    offsetsField="confirmation_offsets"
                    timingFieldLabel="Timing: When to send the message after the reminder is saved"
                    register={register}
                    control={control}
                    errors={errors}
                    messagePreview={confirmationMessagePreview}
                    messageLength={confirmationMessageLength}
                    segmentsCount={confirmationSegmentsCount}
                    timingOptions={filteredConfirmationOffsets}
                    onToggle={handleToggle('confirmation_enabled')}
                    onAddOffset={handleAddCustomOffset}
                    onRemoveOffset={handleRemoveCustomOffset}
                  />

                  <ReminderTypeSection
                    type="reminder"
                    title="Reminder"
                    description="Message to send as the appointment approaches."
                    checked={watch('reminder_enabled')}
                    disabled={isPastEvent}
                    messageField="reminder_message"
                    messageFieldPlaceholder={reminderMessageFieldPlaceholder}
                    messageLanguage={messageLanguage}
                    offsetsField="reminder_offsets"
                    timingFieldLabel="Timing: When to send the message before the appointment starts"
                    register={register}
                    control={control}
                    errors={errors}
                    messagePreview={reminderMessagePreview}
                    messageLength={reminderMessageLength}
                    segmentsCount={reminderSegmentsCount}
                    timingOptions={filteredReminderOffsets}
                    confirmationsEnabled={profileData.appointment_confirmation}
                    onToggle={handleToggle('reminder_enabled')}
                    onAddOffset={handleAddCustomOffset}
                    onRemoveOffset={handleRemoveCustomOffset}
                  />

                  <ReminderTypeSection
                    type="follow_up"
                    title="Follow-up"
                    description="Message to send after the appointment occurs."
                    checked={watch('follow_up_enabled')}
                    disabled={isPastEvent}
                    messageField="follow_up_message"
                    messageFieldPlaceholder={followUpMessageFieldPlaceholder}
                    messageLanguage={messageLanguage}
                    offsetsField="follow_up_offsets"
                    timingFieldLabel="Timing: When to send the message after the appointment completes"
                    register={register}
                    control={control}
                    errors={errors}
                    messagePreview={followUpMessagePreview}
                    messageLength={followUpMessageLength}
                    segmentsCount={followUpSegmentsCount}
                    timingOptions={filteredFollowUpOffsets}
                    onToggle={handleToggle('follow_up_enabled')}
                    onAddOffset={handleAddCustomOffset}
                    onRemoveOffset={handleRemoveCustomOffset}
                  />
                </div>
              </>
            }
          />
          <EditEventTimezonePlaceholderMismatchAlert
            event={event}
            profileData={profileData}
            preferences={preferences}
            calendar={calendar}
            confirmation_message={confirmation_message}
            reminder_message={reminder_message}
            follow_up_message={follow_up_message}
            confirmation_enabled={Boolean(confirmation_enabled)}
            reminder_enabled={Boolean(reminder_enabled)}
            follow_up_enabled={Boolean(follow_up_enabled)}
          />
          {remindersPausedReason ? (
            <EditEventPausedActions reason={remindersPausedReason} saveDisabled={isPastEvent || isSubmitting} />
          ) : (
            <Button
              type="submit"
              className={classes.button}
              widthType="content"
              disabled={isPastEvent}
              isLoading={isSubmitting}
            >
              <FormattedText id="configuration.editReminderModal.button" />
            </Button>
          )}
          {(Object.keys(errors).length > 0 || event.errors.length > 0) && (
            <Typography
              variant="body"
              className={classNames(classes.error, classes.formErrorBottom)}
              style={{ textAlign: 'center' }}
            >
              {joinErrors([...extractErrors(errors), ...event.errors])}
            </Typography>
          )}
        </form>
      </Modal>
      {reviewTimingIssuesState === 'open' && (
        <ReviewTimingIssuesModal
          confirmationEnabled={watch('confirmation_enabled')}
          confirmationOffsets={watch('confirmation_offsets')}
          reminderEnabled={watch('reminder_enabled')}
          reminderOffsets={watch('reminder_offsets')}
          eventDate={eventDate}
          onClose={handleCloseReviewTimingIssuesModal}
          onBack={handleCloseReviewTimingIssuesModal}
          onSend={handleSendReviewTimingIssues}
        />
      )}
      {showCantRemoveModal && <CantRemoveCustomOffsetModal onClose={() => setShowCantRemoveModal(false)} />}
    </>
  )
}

function extractErrors(errors: FieldErrors) {
  if (!errors) return []
  const messages = Object.values(errors).flatMap((error) => {
    if (Array.isArray(error)) {
      return error.flatMap((err) => extractErrors(err))
    } else if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      return [error.message]
    }
    return []
  })
  return Array.from(new Set(messages))
}

function joinErrors(errors: string[]) {
  return (errors || [])
    .map((str) => str.trim())
    .filter(Boolean)
    .join(', ')
}
